import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import { useState, useEffect } from "react";
import {
  Grid,
  MenuItem,
  Chip,
  Autocomplete,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Switch,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";

function CreateDemo() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [slots, setSlots] = useState([]);

  const [formData, setFormData] = useState({
    studentId: "",
    tutorId: "",
    preferredDate: dayjs(), // Initialize with dayjs object for CustomDatePicker
    preferredTimeSlots: [],
    demoType: "",
    notes: "",
    address: "",
    oneTime: false,
    shareParentContactWithTutor: false,
  });

  const [errors, setErrors] = useState({});

  // Helper: extract messages from server responses and show toasts
  const showToastsFromResponse = (payload, type = "error") => {
    try {
      const messages = [];
      const push = (m) => {
        if (m && typeof m === "string") messages.push(m);
      };

      if (!payload) return;

      // Axios error object
      if (payload?.response?.data) {
        return showToastsFromResponse(payload.response.data, type);
      }

      // Direct data object
      push(payload.message);
      push(payload.msg);
      push(payload.error);

      // Array of validation errors (strings or objects)
      if (Array.isArray(payload.errors)) {
        payload.errors.forEach((e) => push(typeof e === "string" ? e : e?.message));
      }

      // Mongoose formatted error
      if (payload.mongooseError) {
        push(payload.mongooseError.message);
        if (Array.isArray(payload.mongooseError.details)) {
          payload.mongooseError.details.forEach((d) => push(d?.message));
        }
      }

      // Fallback if nothing gathered
      if (messages.length === 0 && typeof payload === "string") {
        push(payload);
      }

      if (messages.length === 0) {
        push("Unexpected error");
      }

      messages.forEach((m) => {
        if (type === "success") toast.success(m);
        else if (type === "info") toast.info(m);
        else toast.error(m);
      });
    } catch (_) {
      // As a last resort
      if (type === "success") toast.success("Success");
      else toast.error("Something went wrong");
    }
  };

  // Fetch required data
  useEffect(() => {
    const fetchAllData = async () => {
      setDataLoading(true);
      await Promise.all([fetchStudents(), fetchTutors(), fetchSlots()]);
      setDataLoading(false);
    };
    fetchAllData();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await api.get("/student");
      console.log("Students API response:", response.data);

      // Try multiple possible data structures
      let studentsData = [];
      if (response.data.data?.students) {
        studentsData = response.data.data.students;
      } else if (response.data.data) {
        studentsData = response.data.data;
      } else if (response.data.students) {
        studentsData = response.data.students;
      } else if (Array.isArray(response.data)) {
        studentsData = response.data;
      }

      const validStudents = Array.isArray(studentsData) ? studentsData : [];
      console.log("Valid students:", validStudents);
      setStudents(validStudents);
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudents([]);
      showToastsFromResponse(error);
    }
  };

  const fetchTutors = async () => {
    try {
      const response = await api.get("/tutor");
      console.log("Tutors API response:", response.data);

      // Try multiple possible data structures and normalize _id
      let tutorsData = [];
      if (response.data.data?.tutors) {
        tutorsData = response.data.data.tutors;
      } else if (response.data.data) {
        tutorsData = response.data.data;
      } else if (response.data.tutors) {
        tutorsData = response.data.tutors;
      } else if (Array.isArray(response.data)) {
        tutorsData = response.data;
      }

      const validTutors = (Array.isArray(tutorsData) ? tutorsData : []).map((t) => ({
        ...t,
        _id: t?._id || t?.id || t?.Id || t?._Id || undefined,
      }));
      console.log("Valid tutors:", validTutors);
      setTutors(validTutors);
    } catch (error) {
      console.error("Error fetching tutors:", error);
      setTutors([]);
      showToastsFromResponse(error);
    }
  };

  const fetchSlots = async () => {
    try {
      const response = await api.get("/slots");
      console.log("Slots API response:", response.data);

      // Handle the specific slots response structure
      let slotsData = [];
      if (response.data.data?.slots) {
        slotsData = response.data.data.slots;
      } else if (response.data.data) {
        slotsData = response.data.data;
      } else if (response.data.slots) {
        slotsData = response.data.slots;
      } else if (Array.isArray(response.data)) {
        slotsData = response.data;
      }

      const validSlots = Array.isArray(slotsData) ? slotsData : [];
      console.log("Valid slots:", validSlots);
      setSlots(validSlots);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setSlots([]);
      showToastsFromResponse(error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.studentId) {
      newErrors.studentId = "Student is required";
    }
    if (!formData.tutorId) {
      newErrors.tutorId = "Tutor is required";
    }
    if (!formData.preferredDate || !dayjs(formData.preferredDate).isValid()) {
      newErrors.preferredDate = "Preferred date is required";
    }
    if (formData.preferredTimeSlots.length === 0) {
      newErrors.preferredTimeSlots = "At least one time slot is required";
    }
    if (!formData.demoType) {
      newErrors.demoType = "Demo type is required";
    }
    if (formData.demoType === "OFFLINE" && !formData.address) {
      newErrors.address = "Address is required for offline demos";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Show exact client-side validation errors as separate toasts
      Object.values(validationErrors).forEach((m) => m && toast.error(m));
      return;
    }

    setLoading(true);
    try {
      // Prepare data according to backend API structure
      const demoData = {
        tutorId: formData.tutorId,
        studentId: formData.studentId,
        preferredDate: dayjs(formData.preferredDate).format("YYYY-MM-DD"), // Convert dayjs to string
        preferredTimeSlots: formData.preferredTimeSlots,
        demoType: formData.demoType, // Include demo type
        ...(formData.demoType === "OFFLINE" && { address: formData.address }), // Include address for offline demos
        ...(formData.notes.trim() && { notes: formData.notes }),
        oneTime: formData.oneTime, // Include oneTime field
        shareParentContactWithTutor: formData.shareParentContactWithTutor,
      };

      const response = await api.post("/demo", demoData);

      if (response.data.success) {
        showToastsFromResponse(response.data, "success");
        navigate("/demos");
      } else {
        showToastsFromResponse(response.data, "error");
        // Preserve first message in form submit error for inline display
        const firstMsg =
          response?.data?.message ||
          response?.data?.error ||
          (Array.isArray(response?.data?.errors) ? response.data.errors[0] : undefined) ||
          undefined;
        if (firstMsg) setErrors({ submit: firstMsg });
      }
    } catch (error) {
      console.error("Error creating demo:", error);
      showToastsFromResponse(error, "error");
      const msgInline =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (Array.isArray(error?.response?.data?.errors)
          ? error.response.data.errors[0]
          : undefined) ||
        error?.message ||
        "Network error. Please try again.";
      setErrors({ submit: msgInline });
    } finally {
      setLoading(false);
    }
  };

  const demoTypes = [
    { value: "ONLINE", label: "Online" },
    { value: "OFFLINE", label: "Offline" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <ArgonButton
                    variant="text"
                    color="secondary"
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/demos")}
                  >
                    Back to Demos
                  </ArgonButton>
                  <ArgonTypography variant="h6" fontWeight="medium">
                    Create New Demo
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Student Selection */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Select Student *
                      </ArgonTypography>
                      <Autocomplete
                        options={Array.isArray(students) ? students : []}
                        loading={dataLoading}
                        noOptionsText={dataLoading ? "Loading students..." : "No students found"}
                        getOptionLabel={(option) =>
                          option
                            ? `${option.name || "Unknown"} - ${
                                option.parentId?.phone || "No phone"
                              }`
                            : ""
                        }
                        value={students.find((s) => s._id === formData.studentId) || null}
                        onChange={(event, newValue) => {
                          const sid = newValue?._id || "";
                          handleInputChange("studentId", sid);

                          // Auto-set demoType from student's modeOfTuition
                          const mode = newValue?.modeOfTuition;
                          if (mode === "ONLINE" || mode === "OFFLINE") {
                            handleInputChange("demoType", mode);
                          } else if (mode === "BOTH") {
                            // keep previous selection; if none, default to ONLINE
                            if (!formData.demoType) {
                              handleInputChange("demoType", "ONLINE");
                            }
                          } else if (!newValue) {
                            // if student cleared, reset to default
                            handleInputChange("demoType", "ONLINE");
                          }

                          // Prefill address from parent's city and area
                          if (newValue && newValue.parentId) {
                            const parentCity = newValue.parentId?.city || "";
                            const parentArea = newValue.parentId?.area || "";
                            if (parentCity || parentArea) {
                              const prefillAddress = [parentArea, parentCity]
                                .filter(Boolean)
                                .join(", ");
                              handleInputChange("address", prefillAddress);
                            }
                          } else {
                            // Clear address if no student selected
                            handleInputChange("address", "");
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search and select student"
                            error={!!errors.studentId}
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <ArgonBox>
                              <ArgonTypography variant="button" fontWeight="medium">
                                {option?.name || "Unknown Student"}
                              </ArgonTypography>
                              <ArgonTypography variant="caption" color="secondary" display="block">
                                {option?.parentId?.phone || "No phone"} •{" "}
                                {option?.parentId?.email || "No email"}
                              </ArgonTypography>
                            </ArgonBox>
                          </li>
                        )}
                      />
                    </ArgonBox>
                  </Grid>

                  {/* Tutor Selection */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Select Tutor *
                      </ArgonTypography>
                      <Autocomplete
                        options={Array.isArray(tutors) ? tutors : []}
                        loading={dataLoading}
                        noOptionsText={dataLoading ? "Loading tutors..." : "No tutors found"}
                        getOptionLabel={(option) => {
                          if (!option) return "";
                          const name = option.fullName || option.name || "Unknown";
                          const phone = option.phone || option.phoneNumber || "No phone";
                          return `${name} - ${phone}`;
                        }}
                        isOptionEqualToValue={(option, value) =>
                          (option?._id || option?.id)?.toString() ===
                          (value?._id || value?.id)?.toString()
                        }
                        clearOnEscape
                        disableClearable={false}
                        value={
                          (Array.isArray(tutors) ? tutors : []).find(
                            (t) =>
                              (t?._id || t?.id)?.toString() === (formData.tutorId || "").toString()
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          handleInputChange("tutorId", newValue?._id || newValue?.id || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search and select tutor"
                            error={!!errors.tutorId}
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <ArgonBox>
                              <ArgonTypography variant="button" fontWeight="medium">
                                {option.fullName || option.name || "Unknown"}
                              </ArgonTypography>
                              <ArgonTypography variant="caption" color="secondary" display="block">
                                {option.phone || option.phoneNumber || "No phone"} •{" "}
                                {option.email || "No email"}
                              </ArgonTypography>
                            </ArgonBox>
                          </li>
                        )}
                      />
                    </ArgonBox>
                  </Grid>

                  {/* Preferred Date */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Final Date *
                      </ArgonTypography>
                      <CustomDatePicker
                        value={formData.preferredDate}
                        onChange={(newValue) => handleInputChange("preferredDate", newValue)}
                        width="100%"
                        minDate={dayjs()} // Disable past dates
                        format="DD/MM/YYYY"
                      />
                      {errors.preferredDate && (
                        <ArgonTypography variant="caption" color="error" mt={0.5}>
                          {errors.preferredDate}
                        </ArgonTypography>
                      )}
                    </ArgonBox>
                  </Grid>

                  {/* Demo Type */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Demo Type *
                      </ArgonTypography>
                      <ArgonInput
                        select
                        value={formData.demoType}
                        onChange={(e) => handleInputChange("demoType", e.target.value)}
                        error={!!errors.demoType}
                        disabled={
                          students.find((s) => s._id === formData.studentId)?.modeOfTuition ===
                            "ONLINE" ||
                          students.find((s) => s._id === formData.studentId)?.modeOfTuition ===
                            "OFFLINE"
                        }
                      >
                        {demoTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </ArgonInput>
                      {errors.demoType && (
                        <ArgonTypography variant="caption" color="error" mt={0.5}>
                          {errors.demoType}
                        </ArgonTypography>
                      )}
                    </ArgonBox>
                  </Grid>

                  {/* Time Slots */}
                  <Grid item xs={12}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Preferred Time Slots *
                      </ArgonTypography>
                      <Autocomplete
                        multiple
                        options={Array.isArray(slots) ? slots : []}
                        loading={dataLoading}
                        noOptionsText={
                          dataLoading ? "Loading time slots..." : "No time slots found"
                        }
                        getOptionLabel={(option) =>
                          option
                            ? `${option.startTime || "Unknown"} - ${option.endTime || "Unknown"}`
                            : ""
                        }
                        value={(Array.isArray(slots) ? slots : []).filter((slot) =>
                          formData.preferredTimeSlots.includes(slot._id)
                        )}
                        onChange={(event, newValue) => {
                          handleInputChange(
                            "preferredTimeSlots",
                            (newValue || []).map((slot) => slot._id).filter(Boolean)
                          );
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={`${option?.startTime || "Unknown"} - ${
                                option?.endTime || "Unknown"
                              }`}
                              {...getTagProps({ index })}
                              key={option?._id || index}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select time slots"
                            error={!!errors.preferredTimeSlots}
                            size="small"
                          />
                        )}
                      />
                    </ArgonBox>
                  </Grid>

                  {/* Address (for offline demos) */}
                  {formData.demoType === "OFFLINE" && (
                    <Grid item xs={12}>
                      <ArgonBox mb={2}>
                        <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                          Address *
                        </ArgonTypography>
                        <ArgonInput
                          multiline
                          rows={3}
                          value={formData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          placeholder="Enter full address for offline demo"
                          error={!!errors.address}
                        />
                      </ArgonBox>
                    </Grid>
                  )}

                  {/* Notes */}
                  <Grid item xs={12}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                        Notes (Optional)
                      </ArgonTypography>
                      <ArgonInput
                        multiline
                        rows={3}
                        value={formData.notes}
                        onChange={(e) => handleInputChange("notes", e.target.value)}
                        placeholder="Add any additional notes or requirements"
                      />
                    </ArgonBox>
                  </Grid>

                  {/* One Time Demo */}
                  <Grid item xs={12}>
                    <ArgonBox mb={2}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                            Is this a one-time demo?
                          </ArgonTypography>
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.oneTime ? "yes" : "no"}
                          onChange={(e) => handleInputChange("oneTime", e.target.value === "yes")}
                        >
                          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12}>
                    <ArgonBox mb={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.shareParentContactWithTutor}
                            onChange={(e) =>
                              handleInputChange(
                                "shareParentContactWithTutor",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Share parent contact with tutor"
                      />
                    </ArgonBox>
                  </Grid>

                  {/* Submit Error removed to avoid inline red text */}

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
                      <ArgonButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate("/demos")}
                        disabled={loading}
                      >
                        Cancel
                      </ArgonButton>
                      <ArgonButton
                        type="submit"
                        variant="gradient"
                        color="success"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                      >
                        {loading ? "Creating..." : "Create Demo"}
                      </ArgonButton>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </form>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CreateDemo;
